import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getApiBaseUrl } from "@/lib/config";
import { isLowBandwidth } from "@/hooks/useConnectionQuality";
import { SESSION_EXPIRED_MESSAGE } from "@/lib/authApi";
import type {
  OrderListItem,
  OrderDetail,
  OrderListResponse,
  CreateOrderData,
  UpdateOrderData,
  OrderSearchParams,
  ListingBatch,
  CreateOrderEventData,
  CreateOrderNoteWithAttachmentsData,
  BulkPaymentRequest,
  BulkPaymentResponse,
  SinglePaymentRequest,
  SinglePaymentResponse,
  BulkInvoiceRequest,
  BulkInvoiceResponse,
  BulkQuoteRequest,
  BulkQuoteResponse,
  Location,
  PaymentHistory,
  CashOrder,
  CashOrderSearchParams,
  SquarePaymentResponse,
  SendInvoiceEmailRequest,
  VerifiedImagesResponse,
  OrderEventAttachment,
  PendingVerificationResponse,
  PendingVerificationImage,
} from "@/types/order";

interface ApiError extends Error {
  response?: { data: unknown; status: number };
}

const REQUEST_TIMEOUT_MS = 15000;
const LOW_BANDWIDTH_TIMEOUT_MS = 30000;
/** The pending-verification listing carries photo metadata and is slower to serve. */
const PENDING_VERIFICATION_TIMEOUT_MS = 30000;

const extractErrorMessage = (err: ApiError): string => {
  if (err.response?.data) {
    const data = err.response.data as
      | string
      | string[]
      | { non_field_errors?: string | string[]; [key: string]: unknown };
    if (Array.isArray(data)) {
      return data.join(" ");
    }
    if (typeof data === "string") {
      return data;
    }
    if (data?.non_field_errors && Array.isArray(data.non_field_errors)) {
      return data.non_field_errors.join(" ");
    }
    if (data?.error) {
      return typeof data.error === "string" ? data.error : String(data.error);
    }
    if (data?.detail) {
      return Array.isArray(data.detail) ? data.detail.join(" ") : String(data.detail);
    }
    const firstKey = Object.keys(data)[0];
    if (firstKey && Array.isArray(data[firstKey])) {
      return data[firstKey].join(" ");
    }
  }
  return err.message || "Erreur inconnue";
};

interface OrderEvent {
  id: number;
  event_type: string;
  timestamp: string;
  user_display_name?: string;
  notes?: string;
  attachments?: OrderEventAttachment[];
}

interface PaymentStatusResponse {
  status: string;
  financial_status: string;
  payment_id?: number;
  checkout_id?: string;
  device_id?: string;
  amount?: string;
  message?: string;
  error?: string;
}

interface SendEmailResponse {
  success: boolean;
  message?: string;
}

interface VerifyImageResponse {
  id: number;
  status: string;
}

// Service class that handles all API calls
class OrdersApiService {
  private getAccessToken: () => Promise<string | null>;

  constructor(getAccessToken: () => Promise<string | null>) {
    this.getAccessToken = getAccessToken;
  }

  private async makeRequest(
    endpoint: string,
    options: RequestInit = {},
    timeoutOverrideMs?: number,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<any> {
    const baseUrl = getApiBaseUrl();
    const token = await this.getAccessToken();

    if (!token) {
      throw new Error(SESSION_EXPIRED_MESSAGE);
    }

    // Add timeout to prevent hanging; use longer timeout on low bandwidth
    const controller = new AbortController();
    const timeoutMs =
      timeoutOverrideMs ??
      (isLowBandwidth() ? LOW_BANDWIDTH_TIMEOUT_MS : REQUEST_TIMEOUT_MS);
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(`${baseUrl}${endpoint}`, {
        ...options,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          ...options.headers,
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({
          error: `API request failed: ${response.statusText}`,
        }));
        const error = new Error(
          "An API error occurred. See .response for details.",
        ) as ApiError;
        error.response = { data: errorBody, status: response.status };
        throw error;
      }

      if (response.status === 204) {
        return null;
      }

      return response.json();
    } catch (err) {
      clearTimeout(timeoutId);
      if (err instanceof Error && err.name === "AbortError") {
        throw new Error("Le serveur a mis trop de temps à répondre.");
      }
      throw err;
    }
  }

  private buildOrderQueryParams(
    searchParams: OrderSearchParams,
    extra?: Record<string, string>,
  ): URLSearchParams {
    const queryParams = new URLSearchParams();

    if (searchParams.search) {
      queryParams.append("search", searchParams.search);
    }
    if (searchParams.status && searchParams.status !== "all") {
      queryParams.append("status", searchParams.status);
    }
    if (searchParams.status_exclude) {
      queryParams.append("status_exclude", searchParams.status_exclude);
    }
    if (searchParams.financial_status) {
      queryParams.append("financial_status", searchParams.financial_status);
    }
    if (searchParams.verification_status) {
      queryParams.append(
        "verification_status",
        searchParams.verification_status,
      );
    }
    if (searchParams.listing_only) {
      queryParams.append("listing_only", "true");
    }
    if (searchParams.listing_batches) {
      queryParams.append("listing_batches", searchParams.listing_batches);
    }
    if (searchParams.batch_id) {
      queryParams.append("batch_id", searchParams.batch_id);
    }
    if (searchParams.requires_delivery !== undefined) {
      queryParams.append(
        "requires_delivery",
        searchParams.requires_delivery.toString(),
      );
    }
    if (searchParams.page) {
      queryParams.append("page", searchParams.page.toString());
    }
    if (searchParams.page_size) {
      queryParams.append("page_size", searchParams.page_size.toString());
    }
    if (searchParams.start_date) {
      queryParams.append("start_date", searchParams.start_date);
    }
    if (searchParams.end_date) {
      queryParams.append("end_date", searchParams.end_date);
    }
    if (searchParams.delivery_only) {
      queryParams.append("delivery_only", searchParams.delivery_only);
    }
    if (searchParams.vendor_name) {
      queryParams.append("vendor_name", searchParams.vendor_name);
    }
    if (searchParams.vendor_location) {
      queryParams.append("vendor_location", searchParams.vendor_location);
    }
    if (searchParams.location_id) {
      queryParams.append("location_id", searchParams.location_id);
    }
    if (searchParams.purchase_order_number) {
      queryParams.append(
        "purchase_order_number",
        searchParams.purchase_order_number,
      );
    }
    if (searchParams.delivery_area) {
      queryParams.append("delivery_area", searchParams.delivery_area);
    }
    if (searchParams.has_cash_payment) {
      queryParams.append("has_cash_payment", searchParams.has_cash_payment);
    }
    if (searchParams.ordering) {
      queryParams.append("ordering", searchParams.ordering);
    }
    if (searchParams.account_id) {
      queryParams.append("account_id", searchParams.account_id.toString());
    }
    if (extra) {
      for (const [k, v] of Object.entries(extra)) {
        queryParams.append(k, v);
      }
    }
    return queryParams;
  }

  // Order list methods
  async fetchListingBatches(): Promise<ListingBatch[]> {
    return this.makeRequest("/orders/listings/");
  }

  async fetchOrders(
    searchParams: OrderSearchParams = {},
  ): Promise<OrderListResponse> {
    const queryParams = this.buildOrderQueryParams(searchParams);
    const response = await this.makeRequest(
      `/orders/?${queryParams.toString()}`,
    );
    return response;
  }

  async exportSuiviCsv(
    searchParams: OrderSearchParams,
    mode: "page" | "all",
    format: "xlsx" = "xlsx",
  ): Promise<{ blob: Blob; filename: string | null }> {
    const baseUrl = getApiBaseUrl();
    const token = await this.getAccessToken();
    if (!token) {
      throw new Error(SESSION_EXPIRED_MESSAGE);
    }
    const queryParams = this.buildOrderQueryParams(searchParams, {
      mode,
      export_format: format,
    });
    const resp = await fetch(`${baseUrl}/orders/export/?${queryParams.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!resp.ok) {
      const err = (await resp.json().catch(() => ({}))) as { error?: string };
      throw new Error(err.error || "Échec de l'export");
    }
    const contentDisposition = resp.headers.get("Content-Disposition");
    const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
    return {
      blob: await resp.blob(),
      filename: filenameMatch?.[1] || null,
    };
  }

  // Single order methods
  async fetchOrder(orderId: number): Promise<OrderDetail> {
    const response = await this.makeRequest(`/orders/${orderId}/`);
    return response;
  }

  async fetchCashOrders(
    searchParams: CashOrderSearchParams,
  ): Promise<CashOrder[]> {
    const queryParams = new URLSearchParams();
    queryParams.append("vendor_location", searchParams.vendor_location);
    queryParams.append("start_date", searchParams.start_date);
    if (searchParams.user_id) {
      queryParams.append("user_id", searchParams.user_id);
    }
    const response = await this.makeRequest(
      `/orders/cash-orders/?${queryParams.toString()}`,
    );
    return response;
  }

  async createOrder(orderData: CreateOrderData): Promise<OrderDetail> {
    const response = await this.makeRequest("/orders/", {
      method: "POST",
      body: JSON.stringify(orderData),
    });
    return response;
  }

  async bulkCreateOrders(
    orderData: CreateOrderData[],
  ): Promise<{ order_ids: number[]; batch_id?: string | null }> {
    const response = await this.makeRequest("/orders/bulk_create/", {
      method: "POST",
      body: JSON.stringify(orderData),
    });
    return response;
  }

  async updateOrder(
    orderId: number,
    orderData: UpdateOrderData,
  ): Promise<OrderDetail> {
    const response = await this.makeRequest(`/orders/${orderId}/`, {
      method: "PATCH",
      body: JSON.stringify(orderData),
    });
    return response;
  }

  async deleteOrder(orderId: number): Promise<void> {
    await this.makeRequest(`/orders/${orderId}/`, {
      method: "DELETE",
    });
  }

  // Order events
  async addEvent(
    orderId: number,
    eventData: CreateOrderEventData,
  ): Promise<OrderEvent> {
    const response = await this.makeRequest(`/orders/${orderId}/add_event/`, {
      method: "POST",
      body: JSON.stringify(eventData),
    });
    return response;
  }

  async addNoteWithAttachments(
    orderId: number,
    data: CreateOrderNoteWithAttachmentsData,
  ): Promise<OrderEvent> {
    const response = await this.makeRequest(
      `/orders/${orderId}/add_note_with_attachments/`,
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return response;
  }

  // Payment methods
  async bulkPayment(
    paymentData: BulkPaymentRequest,
  ): Promise<BulkPaymentResponse> {
    const endpoint =
      paymentData.payment_method === "link"
        ? "/orders/generate-payment-link/"
        : "/orders/bulk_payment/";

    const response = await this.makeRequest(endpoint, {
      method: "POST",
      body: JSON.stringify(paymentData),
    });
    return response;
  }

  async terminalPayment(
    orderId: number,
    paymentData: SinglePaymentRequest,
    amount?: number,
  ): Promise<SinglePaymentResponse> {
    const body: SinglePaymentRequest & { amount?: number } = { ...paymentData };
    if (amount !== undefined) {
      body.amount = amount;
    }

    const response = await this.makeRequest(
      `/orders/${orderId}/terminal_payment/`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
    return response;
  }

  async getPaymentStatus(orderId: number): Promise<PaymentStatusResponse> {
    const response = await this.makeRequest(
      `/orders/${orderId}/payment_status/`,
      {
        method: "GET",
      },
    );
    return response;
  }

  async bulkInvoice(
    invoiceData: BulkInvoiceRequest,
  ): Promise<BulkInvoiceResponse> {
    const response = await this.makeRequest("/orders/bulk_invoice/", {
      method: "POST",
      body: JSON.stringify(invoiceData),
    });
    return response;
  }

  async markInvoicePaid(orderId: number): Promise<{
    message: string;
    order_id: number;
    financial_status: string;
  }> {
    const response = await this.makeRequest(
      `/orders/${orderId}/mark-invoice-paid/`,
      {
        method: "POST",
      },
    );
    return response;
  }

  async getReceiptUrl(orderId: number): Promise<SquarePaymentResponse> {
    const response = await this.makeRequest(
      `/orders/${orderId}/square-payment/`,
      {
        method: "GET",
      },
    );
    return response;
  }

  async issueTerminalReceipt(
    orderId: number,
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    const response = await this.makeRequest(
      `/orders/${orderId}/terminal-receipt/`,
      {
        method: "POST",
      },
    );
    return response;
  }

  async printReceipt(orderId: number): Promise<{
    success: boolean;
    message?: string;
    error?: string;
    job_id?: number;
    printer?: string;
  }> {
    const response = await this.makeRequest(
      `/orders/${orderId}/print-receipt/`,
      {
        method: "POST",
      },
    );
    return response;
  }

  async printOrderSlip(orderId: number): Promise<{
    success: boolean;
    message?: string;
    error?: string;
    job_id?: number;
    printer?: string;
  }> {
    const response = await this.makeRequest(
      `/orders/${orderId}/print-order-slip/`,
      {
        method: "POST",
      },
    );
    return response;
  }

  async sendInvoiceEmail(
    body: SendInvoiceEmailRequest,
  ): Promise<SendEmailResponse> {
    const response = await this.makeRequest(`/orders/send-receipt-email/`, {
      method: "POST",
      body: JSON.stringify(body),
    });
    return response;
  }

  async sendPaidInvoiceEmail(
    body: SendInvoiceEmailRequest,
  ): Promise<SendEmailResponse> {
    const response = await this.makeRequest(
      `/orders/send-paid-invoice-email/`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
    return response;
  }

  async bulkQuote(quoteData: BulkQuoteRequest): Promise<BulkQuoteResponse> {
    const response = await this.makeRequest("/orders/bulk_quote/", {
      method: "POST",
      body: JSON.stringify(quoteData),
    });

    const rawResponse = response as Record<string, unknown>;
    const normalized: Partial<BulkQuoteResponse> & Record<string, unknown> = {
      ...rawResponse,
    };

    if (rawResponse?.quote) {
      const quote =
        rawResponse.quote as BulkQuoteResponse["successful_quotes"][0];
      normalized.quotes =
        (normalized.quotes as BulkQuoteResponse["successful_quotes"]) ?? [
          quote,
        ];
      normalized.successful_quotes =
        (normalized.successful_quotes as BulkQuoteResponse["successful_quotes"]) ?? [
          quote,
        ];
      if (!normalized.quote_id && quote.id) {
        normalized.quote_id = quote.id;
      }
    }

    if (rawResponse?.order_ids) {
      normalized.quoted_order_ids =
        (normalized.quoted_order_ids as number[]) ??
        (rawResponse.order_ids as number[]);
    }

    if (!Array.isArray(normalized.failed_orders)) {
      normalized.failed_orders = [];
    }

    return normalized as BulkQuoteResponse;
  }

  async generatePaymentLink(
    orderId: number,
  ): Promise<{ payment_link?: string }> {
    const response = await this.makeRequest(
      `/orders/${orderId}/generate-payment-link/`,
      {
        method: "POST",
        body: JSON.stringify({}),
      },
    );
    return response;
  }

  async sendPaymentLinkEmail(
    orderId: number,
  ): Promise<{ success: boolean; message?: string }> {
    const response = await this.makeRequest(
      `/orders/${orderId}/send-payment-link-email/`,
      {
        method: "POST",
        body: JSON.stringify({}),
      },
    );
    return response;
  }

  async verifyOrderImage(
    orderImageId: number,
    action: "approve" | "reject",
    comment?: string,
    imageId?: number,
  ): Promise<VerifyImageResponse> {
    const payload: { action: string; comment?: string; image_id?: number } = {
      action,
    };
    if (comment) {
      payload.comment = comment;
    }
    if (imageId) {
      payload.image_id = imageId;
    }
    const response = await this.makeRequest(
      `/order-images/${orderImageId}/verify/`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );
    return response;
  }

  async getVerifiedImages(params?: {
    status?: "approved" | "rejected" | "all";
    employee?: string;
    page?: number;
    page_size?: number;
  }): Promise<VerifiedImagesResponse> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append("status", params.status);
    if (params?.employee) searchParams.append("employee", params.employee);
    if (params?.page) searchParams.append("page", params.page.toString());
    if (params?.page_size)
      searchParams.append("page_size", params.page_size.toString());

    const queryString = searchParams.toString();
    const url = `/order-images/verified/${queryString ? `?${queryString}` : ""}`;
    return this.makeRequest(url);
  }

  async getPendingVerificationImages(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PendingVerificationResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append("page", params.page.toString());
    if (params?.page_size)
      searchParams.append("page_size", params.page_size.toString());

    const queryString = searchParams.toString();
    const url = `/order-images/pending/${queryString ? `?${queryString}` : ""}`;
    return this.makeRequest(url, {}, PENDING_VERIFICATION_TIMEOUT_MS);
  }

  async getUploaders(): Promise<string[]> {
    return this.makeRequest("/order-images/uploaders/");
  }

  async revertVerification(orderImageId: number): Promise<{ status: string }> {
    return this.makeRequest(`/order-images/${orderImageId}/revert/`, {
      method: "POST",
    });
  }

  // Order images
  async addImageToOrder(
    orderId: number,
    imageId: number,
    caption?: string,
    replaces_image_id?: number | null,
    verification_status?: "pending" | "approved" | "rejected",
    author_name?: string,
  ): Promise<OrderDetail> {
    const body: {
      image_id: number;
      caption: string;
      replaces_image_id?: number | null;
      verification_status?: "pending" | "approved" | "rejected";
      author_name?: string;
    } = {
      image_id: imageId,
      caption: caption || "",
    };
    if (replaces_image_id) {
      body.replaces_image_id = replaces_image_id;
    }
    if (verification_status) {
      body.verification_status = verification_status;
    }
    if (author_name) {
      body.author_name = author_name;
    }
    const response = await this.makeRequest(`/orders/${orderId}/images/`, {
      method: "POST",
      body: JSON.stringify(body),
    });
    return response;
  }

  async removeImageFromOrder(orderId: number, imageId: number): Promise<void> {
    await this.makeRequest(`/orders/${orderId}/images/${imageId}/`, {
      method: "DELETE",
    });
  }

  // Verification delegation
  async getVerificationDelegation(): Promise<{
    delegation: {
      id: number;
      delegated_to: {
        id: string;
        display_name: string;
        email: string;
      } | null;
      delegated_by: {
        id: string;
        display_name: string;
        email: string;
      } | null;
      created_at: string;
    } | null;
  }> {
    const response = await this.makeRequest(`/verification/delegation/`, {
      method: "GET",
    });
    return response;
  }

  async delegateVerification(delegatedToUserId: string): Promise<{
    delegation: {
      id: number;
      delegated_to_user_id: string;
      delegated_by_user_id: string;
      created_at: string;
    };
  }> {
    const response = await this.makeRequest(`/verification/delegate/`, {
      method: "POST",
      body: JSON.stringify({ delegated_to_user_id: delegatedToUserId }),
    });
    return response;
  }

  async takeOverVerification(): Promise<{
    message: string;
    delegations_ended: number;
  }> {
    const response = await this.makeRequest(`/verification/take-over/`, {
      method: "POST",
    });
    return response;
  }

  // Location methods
  async fetchLocations(): Promise<Location[]> {
    const response = await this.makeRequest("/locations/");
    return response;
  }

  // Delivery distance calculation
  async calculateDeliveryDistance(
    vendorLocation: string,
    address: string,
    postalCode: string,
    city: string,
  ): Promise<{ distance_km: number; price: number; origin_address: string }> {
    const response = await this.makeRequest(
      "/orders/delivery/calculate-distance/",
      {
        method: "POST",
        body: JSON.stringify({
          vendor_location: vendorLocation,
          destination_address: address,
          destination_postal_code: postalCode,
          destination_city: city,
        }),
      },
    );
    return response;
  }

  async verifyPin(
    pinCode: string,
  ): Promise<{ success: boolean; name: string }> {
    const response = await this.makeRequest("/team/verify-pin/", {
      method: "POST",
      body: JSON.stringify({ pin_code: pinCode }),
    });
    return response;
  }

  async recordCashPayment(
    orderId: number,
    tenderedAmount: number,
    paymentAmount: number,
    locationId?: number,
  ): Promise<{
    change_due: string;
    remaining_balance: string;
    is_fully_paid: boolean;
  }> {
    const payload: Record<string, number> = {
      tendered_amount: tenderedAmount,
      payment_amount: paymentAmount,
    };
    if (locationId !== undefined && locationId !== null) {
      payload.location_id = locationId;
    }
    const response = await this.makeRequest(
      `/orders/${orderId}/record_cash_payment/`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );
    return response;
  }

  async getPaymentHistory(orderId: number): Promise<PaymentHistory> {
    return this.makeRequest(`/orders/${orderId}/payment-history/`);
  }

  async cancelPayment(orderId: number): Promise<void> {
    await this.makeRequest(`/orders/${orderId}/cancel-payment/`, {
      method: "POST",
    });
  }

  async cancelPaymentLink(orderId: number): Promise<void> {
    await this.makeRequest(`/orders/${orderId}/cancel-payment-link/`, {
      method: "POST",
    });
  }

  async sendQuote(orderId: number): Promise<void> {
    await this.makeRequest(`/orders/${orderId}/send-pennylane-quote/`, {
      method: "POST",
    });
  }

  async reassignLocation(
    orderId: number,
    locationId: number,
  ): Promise<OrderDetail> {
    const response = await this.makeRequest(
      `/orders/${orderId}/reassign-location/`,
      {
        method: "POST",
        body: JSON.stringify({ location_id: locationId }),
      },
    );
    return response;
  }
}

// Hook to get the service instance
export function useOrdersApiService() {
  const { getAccessToken } = useAuth();
  const service = useMemo(
    () => new OrdersApiService(getAccessToken),
    [getAccessToken],
  );
  return service;
}

// State management hooks (no automatic API calls)
export function useOrdersList(initialParams: OrderSearchParams = {}) {
  const defaultParams = { page_size: 20, page: 1, ...initialParams };

  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [params, setParams] = useState<OrderSearchParams>(defaultParams);
  const paramsRef = useRef<OrderSearchParams>(defaultParams);

  const service = useOrdersApiService();

  useEffect(() => {
    paramsRef.current = params;
  }, [params]);

  const fetchOrders = useCallback(
    async (searchParams?: OrderSearchParams, append = false) => {
      try {
        setLoading(true);
        setError(null);

        // Use provided params or current params
        const paramsToUse = searchParams || paramsRef.current;
        const response = await service.fetchOrders(paramsToUse);

        if (append) {
          setOrders((prev) => {
            const existingIds = new Set(prev.map((order) => order.id));
            const newOrders = response.results.filter(
              (order) => !existingIds.has(order.id),
            );
            return [...prev, ...newOrders];
          });
        } else {
          setOrders(response.results);
        }

        setTotalCount(response.count);
        setHasMore(!!response.next);

        // Update params if they were provided
        if (searchParams) {
          paramsRef.current = searchParams;
          setParams(searchParams);
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Échec du chargement des commandes",
        );
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  const updateParams = useCallback(
    (newParams: Partial<OrderSearchParams>) => {
      const updatedParams = { ...paramsRef.current, ...newParams, page: 1 };
      paramsRef.current = updatedParams;
      setParams(updatedParams);
      fetchOrders(updatedParams);
    },
    [fetchOrders],
  );

  const loadMore = useCallback(async () => {
    if (hasMore && !loading) {
      const nextPage = (paramsRef.current.page || 1) + 1;
      const newParams = { ...paramsRef.current, page: nextPage };
      paramsRef.current = newParams;
      await fetchOrders(newParams, true);
    }
  }, [hasMore, loading, fetchOrders]);

  const refresh = useCallback(async () => {
    const refreshParams = { ...paramsRef.current, page: 1 };
    paramsRef.current = refreshParams;
    await fetchOrders(refreshParams);
  }, [fetchOrders]);

  return {
    orders,
    loading,
    error,
    totalCount,
    hasMore,
    params,
    updateParams,
    loadMore,
    refresh,
    setOrders,
    fetchOrders,
  };
}

export function usePendingVerificationQueue(initialPageSize = 20) {
  const [pendingImages, setPendingImages] = useState<PendingVerificationImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const service = useOrdersApiService();

  const fetchPendingImages = useCallback(
    async (params?: {
      page?: number;
      page_size?: number;
    }): Promise<PendingVerificationResponse | null> => {
      try {
        setLoading(true);
        setError(null);
        const response = await service.getPendingVerificationImages({
          page: 1,
          page_size: initialPageSize,
          ...params,
        });
        setPendingImages(response.results);
        setTotalCount(response.count);
        return response;
      } catch (err) {
        const errorMessage =
          extractErrorMessage(err as ApiError) ||
          "Échec du chargement des vérifications";
        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [initialPageSize, service],
  );

  const refresh = useCallback(async () => {
    return fetchPendingImages();
  }, [fetchPendingImages]);

  const removePendingImage = useCallback((imageId: number) => {
    setPendingImages((prev) => prev.filter((image) => image.id !== imageId));
    setTotalCount((prev) => Math.max(prev - 1, 0));
  }, []);

  return {
    pendingImages,
    loading,
    error,
    totalCount,
    fetchPendingImages,
    refresh,
    removePendingImage,
    setPendingImages,
  };
}

export function useOrderDetail(orderId: number | null) {
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(!!orderId);
  const [error, setError] = useState<string | null>(null);

  const service = useOrdersApiService();

  const fetchOrder = useCallback(
    async (id: number) => {
      if (!id) {
        setOrder(null);
        return null;
      }
      try {
        setLoading(true);
        setError(null);
        const orderData = await service.fetchOrder(id);
        setOrder(orderData);
        return orderData;
      } catch (err) {
        console.error("Failed to fetch order:", err);
        setError(
          extractErrorMessage(err as ApiError) || "Échec du chargement de la commande",
        );
        setOrder(null);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  useEffect(() => {
    if (orderId) {
      fetchOrder(orderId);
    } else {
      setOrder(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const refresh = useCallback(async () => {
    if (orderId) {
      await fetchOrder(orderId);
    }
  }, [orderId, fetchOrder]);

  return {
    order,
    loading,
    error,
    fetchOrder,
    refresh,
    setOrder,
  };
}

export function useOrdersApi() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const service = useOrdersApiService();

  const createOrder = useCallback(
    async (orderData: CreateOrderData): Promise<OrderDetail> => {
      if (
        orderData.requires_delivery &&
        !orderData.payer_account &&
        !orderData.recipient_account
      ) {
        throw new Error(
          "Ajouter un payeur ou un destinataire avant de choisir la livraison.",
        );
      }
      try {
        setLoading(true);
        setError(null);
        const response = await service.createOrder(orderData);
        return response;
      } catch (err) {
        const apiErr = err as ApiError;
        const errorMessage =
          extractErrorMessage(apiErr) || "Échec de la création de la commande";
        setError(errorMessage);
        // Preserve response payload for UI handling
        apiErr.message = errorMessage;
        throw apiErr;
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  const updateOrder = useCallback(
    async (
      orderId: number,
      orderData: UpdateOrderData,
    ): Promise<OrderDetail> => {
      try {
        setLoading(true);
        setError(null);
        const response = await service.updateOrder(orderId, orderData);
        return response;
      } catch (err) {
        const apiErr = err as ApiError;
        const errorMessage =
          extractErrorMessage(apiErr) || "Échec de la mise à jour de la commande";
        setError(errorMessage);
        // Preserve response payload for UI handling
        apiErr.message = errorMessage;
        throw apiErr;
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  const deleteOrder = useCallback(
    async (orderId: number): Promise<void> => {
      try {
        setLoading(true);
        setError(null);
        await service.deleteOrder(orderId);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Échec de la suppression de la commande";
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  const addEvent = useCallback(
    async (
      orderId: number,
      eventData: CreateOrderEventData,
    ): Promise<OrderEvent> => {
      try {
        setLoading(true);
        setError(null);
        const response = await service.addEvent(orderId, eventData);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Échec de l'ajout de l'événement";
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  const bulkPayment = useCallback(
    async (paymentData: BulkPaymentRequest): Promise<BulkPaymentResponse> => {
      try {
        setLoading(true);
        setError(null);
        const response = await service.bulkPayment(paymentData);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Échec du traitement du paiement groupé";
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  const bulkInvoice = useCallback(
    async (invoiceData: BulkInvoiceRequest): Promise<BulkInvoiceResponse> => {
      try {
        setLoading(true);
        setError(null);
        const response = await service.bulkInvoice(invoiceData);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Échec du traitement de la facture groupée";
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  const markInvoicePaid = useCallback(
    async (orderId: number): Promise<{
      message: string;
      order_id: number;
      financial_status: string;
    }> => {
      try {
        setLoading(true);
        setError(null);
        const response = await service.markInvoicePaid(orderId);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Échec du marquage de la facture comme payée";
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  const getReceiptUrl = useCallback(
    async (orderId: number): Promise<SquarePaymentResponse> => {
      try {
        setLoading(true);
        setError(null);
        const response = await service.getReceiptUrl(orderId);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Échec de la récupération du reçu";
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  const issueTerminalReceipt = useCallback(
    async (
      orderId: number,
    ): Promise<{ success: boolean; message?: string; error?: string }> => {
      try {
        setLoading(true);
        setError(null);
        const response = await service.issueTerminalReceipt(orderId);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Échec de l'émission du reçu sur le terminal";
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  const printReceipt = useCallback(
    async (
      orderId: number,
    ): Promise<{
      success: boolean;
      message?: string;
      error?: string;
      job_id?: number;
      printer?: string;
    }> => {
      try {
        setLoading(true);
        setError(null);
        const response = await service.printReceipt(orderId);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Échec de la création du travail d'impression";
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  const printOrderSlip = useCallback(
    async (
      orderId: number,
    ): Promise<{
      success: boolean;
      message?: string;
      error?: string;
      job_id?: number;
      printer?: string;
    }> => {
      try {
        setLoading(true);
        setError(null);
        const response = await service.printOrderSlip(orderId);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Échec de la création du travail d'impression";
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  const sendInvoiceEmail = useCallback(
    async (body: SendInvoiceEmailRequest): Promise<SendEmailResponse> => {
      try {
        setLoading(true);
        setError(null);
        const response = await service.sendInvoiceEmail(body);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Échec de l'envoi de l'email de la facture";
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  const sendPaidInvoiceEmail = useCallback(
    async (body: SendInvoiceEmailRequest): Promise<SendEmailResponse> => {
      try {
        setLoading(true);
        setError(null);
        const response = await service.sendPaidInvoiceEmail(body);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Échec de l'envoi de l'email de la facture acquittée";
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  const bulkQuote = useCallback(
    async (quoteData: BulkQuoteRequest): Promise<BulkQuoteResponse> => {
      try {
        setLoading(true);
        setError(null);
        const response = await service.bulkQuote(quoteData);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Échec de la génération du devis";
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  const terminalPayment = useCallback(
    async (
      orderId: number,
      paymentData: SinglePaymentRequest,
      amount?: number,
    ): Promise<SinglePaymentResponse> => {
      try {
        setLoading(true);
        setError(null);
        const response = await service.terminalPayment(
          orderId,
          paymentData,
          amount,
        );
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Échec du traitement du paiement";
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  const getPaymentStatus = useCallback(
    async (orderId: number): Promise<PaymentStatusResponse> => {
      try {
        const response = await service.getPaymentStatus(orderId);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Échec de la récupération du statut de paiement";
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    [service],
  );

  const getPaymentHistory = useCallback(
    async (orderId: number): Promise<PaymentHistory> => {
      try {
        const response = await service.getPaymentHistory(orderId);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Échec du chargement de l'historique des paiements";
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    [service],
  );

  const generatePaymentLink = useCallback(
    async (orderId: number): Promise<{ payment_link?: string }> => {
      try {
        setLoading(true);
        setError(null);
        const response = await service.generatePaymentLink(orderId);
        return response;
      } catch (err) {
        // Preserve the original error structure for proper error message extraction
        const apiError = err as ApiError;
        const errorMessage = extractErrorMessage(apiError) || 
          (err instanceof Error ? err.message : "Échec de la génération du lien de paiement");
        setError(errorMessage);
        // Re-throw the original ApiError to preserve error.response structure
        // If it's not an ApiError, wrap it but preserve the message
        if (apiError.response) {
          throw apiError;
        }
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  const sendPaymentLinkEmail = useCallback(
    async (
      orderId: number,
    ): Promise<{ success: boolean; message?: string }> => {
      try {
        setLoading(true);
        setError(null);
        const response = await service.sendPaymentLinkEmail(orderId);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Échec de l'envoi de l'email";
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  const addImageToOrder = useCallback(
    async (
      orderId: number,
      imageId: number,
      caption?: string,
      replaces_image_id?: number | null,
      verification_status?: "pending" | "approved" | "rejected",
      author_name?: string,
    ): Promise<OrderDetail> => {
      try {
        setLoading(true);
        setError(null);
        const response = await service.addImageToOrder(
          orderId,
          imageId,
          caption,
          replaces_image_id,
          verification_status,
          author_name,
        );
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Échec de l'ajout de l'image";
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  const removeImageFromOrder = useCallback(
    async (orderId: number, imageId: number): Promise<void> => {
      try {
        setLoading(true);
        setError(null);
        await service.removeImageFromOrder(orderId, imageId);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Échec de la suppression de l'image";
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  const bulkCreateOrders = useCallback(
    async (
      orderData: CreateOrderData[],
    ): Promise<{ order_ids: number[]; batch_id?: string | null }> => {
      try {
        setLoading(true);
        setError(null);
        const response = await service.bulkCreateOrders(orderData);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Échec de la création des commandes";
        setError(errorMessage);
        // Keep `response` so callers can show the server's per-order messages.
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  const verifyOrderImage = useCallback(
    async (
      orderImageId: number,
      action: "approve" | "reject",
      comment?: string,
      imageId?: number,
    ): Promise<VerifyImageResponse> => {
      try {
        setLoading(true);
        setError(null);
        const response = await service.verifyOrderImage(
          orderImageId,
          action,
          comment,
          imageId,
        );
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Échec de la vérification de l'image";
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  const getVerifiedImages = useCallback(
    async (params?: {
      status?: "approved" | "rejected" | "all";
      employee?: string;
      page?: number;
      page_size?: number;
    }): Promise<VerifiedImagesResponse> => {
      try {
        setLoading(true);
        setError(null);
        const response = await service.getVerifiedImages(params);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Échec de la récupération des images vérifiées";
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  const getUploaders = useCallback(async (): Promise<string[]> => {
    try {
      return await service.getUploaders();
    } catch (err) {
      console.error("Failed to fetch uploaders:", err);
      return [];
    }
  }, [service]);

  const revertVerification = useCallback(
    async (orderImageId: number): Promise<{ status: string }> => {
      try {
        setLoading(true);
        setError(null);
        const response = await service.revertVerification(orderImageId);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Échec de l'annulation de la vérification";
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  const recordCashPayment = useCallback(
    async (
      orderId: number,
      tenderedAmount: number,
      paymentAmount: number,
      locationId?: number,
    ): Promise<{
      change_due: string;
      remaining_balance: string;
      is_fully_paid: boolean;
    }> => {
      try {
        setLoading(true);
        setError(null);
        const response = await service.recordCashPayment(
          orderId,
          tenderedAmount,
          paymentAmount,
          locationId,
        );
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Échec de l'enregistrement du paiement en espèces";
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  const sendQuote = useCallback(
    async (orderId: number): Promise<void> => {
      try {
        setLoading(true);
        setError(null);
        await service.sendQuote(orderId);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Échec de l'envoi du devis";
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  const reassignLocation = useCallback(
    async (orderId: number, locationId: number): Promise<OrderDetail> => {
      try {
        setLoading(true);
        setError(null);
        const response = await service.reassignLocation(orderId, locationId);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Échec de la réassignation de la boutique";
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  const getVerificationDelegation = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await service.getVerificationDelegation();
      return response;
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Échec de la récupération de la délégation";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [service]);

  const delegateVerification = useCallback(
    async (delegatedToUserId: string) => {
      try {
        setLoading(true);
        setError(null);
        const response = await service.delegateVerification(delegatedToUserId);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Échec de la délégation de vérification";
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  const takeOverVerification = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await service.takeOverVerification();
      return response;
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Échec de la reprise de vérification";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [service]);

  return {
    createOrder,
    bulkCreateOrders,
    updateOrder,
    deleteOrder,
    addEvent,
    bulkPayment,
    bulkInvoice,
    markInvoicePaid,
    bulkQuote,
    terminalPayment,
    getPaymentStatus,
    cancelPayment: service.cancelPayment.bind(service),
    cancelPaymentLink: service.cancelPaymentLink.bind(service),
    getPaymentHistory,
    generatePaymentLink,
    sendPaymentLinkEmail,
    sendInvoiceEmail,
    sendPaidInvoiceEmail,
    getReceiptUrl,
    issueTerminalReceipt,
    printReceipt,
    printOrderSlip,
    addImageToOrder,
    removeImageFromOrder,
    verifyOrderImage,
    getVerifiedImages,
    getUploaders,
    revertVerification,
    recordCashPayment,
    sendQuote,
    reassignLocation,
    getVerificationDelegation,
    delegateVerification,
    takeOverVerification,
    calculateDeliveryDistance: service.calculateDeliveryDistance.bind(service),
    loading,
    error,
  };
}

export function useLocations() {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const service = useOrdersApiService();

  useEffect(() => {
    const loadLocations = async () => {
      try {
        setLoading(true);
        setError(null);
        const locationsData = await service.fetchLocations();
        setLocations(locationsData);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Échec de la récupération des emplacements",
        );
      } finally {
        setLoading(false);
      }
    };

    loadLocations();
  }, [service]);

  return {
    locations,
    loading,
    error,
  };
}
